<template>
  <div>
    <div class="row">
      <div class="col-12 mt-3">
        <Card body-classes="p-3" card-classes="shadow-sm">
          <template #header>
            <div class="d-flex flex-row align-items-center justify-content-between">
              <h5 class="mb-0 text-primary">
                <vue-feather type="award" class="me-2"></vue-feather>
                Product Performance - {{ store.name }}
              </h5>
              <div class="d-flex align-items-center gap-3">
                <div class="d-flex align-items-center">
                  <label for="fromDate" class="me-2 text-muted">From:</label>
                  <input type="date" id="fromDate" class="form-control form-control-sm" v-model="fromDate">
                </div>
                <div class="d-flex align-items-center">
                  <label for="toDate" class="me-2 text-muted">To:</label>
                  <input type="date" id="toDate" class="form-control form-control-sm" v-model="toDate">
                </div>
                <div class="d-flex align-items-center">
                  <label for="limit" class="me-2 text-muted">Top:</label>
                  <input type="number" id="limit" class="form-control form-control-sm" v-model="limit" min="5" max="100" style="width: 80px;">
                </div>
                <button class="btn btn-primary btn-sm" @click="getReport">
                  Generate Report
                </button>
              </div>
            </div>
          </template>
          <div>
            <div v-if="loading" class="text-center py-5">
              <Spinner spinner-class="mb-1" />
              <p class="text-muted mt-2">Loading product performance...</p>
            </div>
            <div v-else-if="reportData.length > 0">
              <div class="row mb-4">
                <div class="col-md-3">
                  <div class="card shadow-sm">
                    <div class="card-body text-center">
                      <vue-feather type="dollar-sign" size="24" class="text-success mb-2"></vue-feather>
                      <h6 class="text-muted mb-1">Total Revenue</h6>
                      <h4 class="mb-0 text-success">{{ formatNumber(totalRevenue) }}</h4>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="card shadow-sm">
                    <div class="card-body text-center">
                      <vue-feather type="trending-up" size="24" class="text-primary mb-2"></vue-feather>
                      <h6 class="text-muted mb-1">Total Profit</h6>
                      <h4 class="mb-0 text-primary">{{ formatNumber(totalProfit) }}</h4>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="card shadow-sm">
                    <div class="card-body text-center">
                      <vue-feather type="package" size="24" class="text-warning mb-2"></vue-feather>
                      <h6 class="text-muted mb-1">Total Qty Sold</h6>
                      <h4 class="mb-0 text-warning">{{ totalQuantity }}</h4>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="card shadow-sm">
                    <div class="card-body text-center">
                      <vue-feather type="shopping-bag" size="24" class="text-info mb-2"></vue-feather>
                      <h6 class="text-muted mb-1">Total Sales</h6>
                      <h4 class="mb-0 text-info">{{ totalSales }}</h4>
                    </div>
                  </div>
                </div>
              </div>
              <div class="table-responsive">
                <table class="table table-hover">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>SKU</th>
                      <th>Product Name</th>
                      <th>Category</th>
                      <th>Qty Sold</th>
                      <th>Revenue</th>
                      <th>Profit</th>
                      <th>Avg Price</th>
                      <th>Stock</th>
                      <th>Sales</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, index) in reportData" :key="item.id">
                      <td>{{ index + 1 }}</td>
                      <td>{{ item.sku || 'N/A' }}</td>
                      <td>{{ item.name }}</td>
                      <td>{{ item.category_name || 'N/A' }}</td>
                      <td>{{ item.quantity_sold }}</td>
                      <td>{{ formatNumber(item.total_revenue) }}</td>
                      <td>
                        <span class="text-success">{{ formatNumber(item.total_profit) }}</span>
                      </td>
                      <td>{{ formatNumber(item.average_price) }}</td>
                      <td>
                        <span :class="getStockStatusClass(item.current_stock)">
                          {{ item.current_stock }}
                        </span>
                      </td>
                      <td>{{ item.number_of_sales }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div v-else class="alert alert-info">
              <vue-feather type="info" size="18" class="me-2"></vue-feather>
              No data available for the selected period.
            </div>
          </div>
        </Card>
      </div>
    </div>
  </div>
</template>

<script>
import {decrypt} from "@/utils/crypto";
import analyticsService from "@/api/analyticsService";
import formatNumber from "@/utils/formatNumber";

export default {
  name: "ProductPerformance",

  data(){
    return {
      profile: {},
      store: {},
      loading: false,
      fromDate: '',
      toDate: '',
      limit: 20,
      reportData: []
    }
  },

  methods: {
    formatNumber,
    async getReport() {
      if (!this.fromDate || !this.toDate) {
        return;
      }

      this.loading = true;
      try {
        const params = {
          store_id: this.store.id,
          from: this.fromDate,
          to: this.toDate,
          limit: this.limit
        };

        const response = await analyticsService.getProductPerformance(params);
        this.reportData = response || [];
      } catch(e) {
        console.error(e);
        this.reportData = [];
      } finally {
        this.loading = false;
      }
    },
    getStockStatusClass(stock) {
      if (stock === 0) return 'badge bg-danger';
      if (stock < 10) return 'badge bg-warning';
      return 'badge bg-success';
    },
    setDefaultDates() {
      const today = new Date();
      const year = today.getFullYear();
      const month = today.getMonth();
      
      const firstDay = new Date(year, month, 1);
      const fromYear = firstDay.getFullYear();
      const fromMonth = String(firstDay.getMonth() + 1).padStart(2, '0');
      const fromDay = String(firstDay.getDate()).padStart(2, '0');
      
      const toYear = today.getFullYear();
      const toMonth = String(today.getMonth() + 1).padStart(2, '0');
      const toDay = String(today.getDate()).padStart(2, '0');

      this.fromDate = `${fromYear}-${fromMonth}-${fromDay}`;
      this.toDate = `${toYear}-${toMonth}-${toDay}`;
    }
  },

  computed: {
    totalRevenue() {
      return this.reportData.reduce((sum, item) => sum + parseFloat(item.total_revenue || 0), 0);
    },
    totalProfit() {
      return this.reportData.reduce((sum, item) => sum + parseFloat(item.total_profit || 0), 0);
    },
    totalQuantity() {
      return this.reportData.reduce((sum, item) => sum + parseFloat(item.quantity_sold || 0), 0);
    },
    totalSales() {
      return this.reportData.reduce((sum, item) => sum + parseFloat(item.number_of_sales || 0), 0);
    }
  },

  created: async function () {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')));
    this.store = JSON.parse(decrypt(sessionStorage.getItem('store')));
    this.setDefaultDates();
    await this.getReport();
  }
}
</script>

<style scoped>
.table th {
  background-color: #f8f9fa;
  font-weight: 600;
}
</style>
